import React, { useState } from 'react'
import useStore from 'state/knovStore'
import styles from 'components/quests/quest-options.module.scss'
import { OptionList, Option } from 'components/quests/QuestOptions'
import perms from 'lib/perms'
import cn from 'classnames'
import api from 'api/api'
import TypeOptions from 'components/quests/TypeOptions'
import queryClient from 'api/queryClient'
import { isMobile } from 'lib/device'
import { withDepth } from 'components/filters/useStreamFilters'
import { getQuestsQueryKey } from 'refactor/hooks/api/useGetQuests'
import { resetNaturalFeedSessionSnapshot } from 'lib/naturalFeedSession'

import { cacheQuest } from 'state/cache'
import useOpenRight from 'refactor/hooks/useOpenRight'
import QuestOptionsHeader from 'components/quests/QuestOptionsHeader'
import {
    isUserMuted,
    removeMutedUser,
    addMutedUser,
    isQuestMuted,
    removeMutedQuest,
    addMutedQuest,
    isStreamMuted,
    addMutedStream,
    removeMutedStream,
} from 'utils/muteUtils'
import {
    Bell,
    BellOff,
    Copy,
    Download,
    MessageSquare,
    Mic,
    Pin,
    Play,
    Send,
    Trash2,
    Volume2,
    VolumeX,
} from 'lucide-react'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'

/**
 * @typedef {{type: string, name: string, value: string, desc?: string, logo?: string, icon?: React.ReactNode, callback: () => void, closeOnClick?: boolean}} OptionItem
 * @typedef {{customComp: React.ReactNode}} CustomOptionItem
 */

// User/thread mute entries, extractable as their own list section via the
// onlyMuteOptions / hideMuteOptions props (the answer controls popup renders
// them in a different spot than the rest of the thread actions).
const MUTE_OPTION_VALUES = ['mute_user', 'unmute_user', 'mute_quest', 'unmute_quest']

export default function ExtendedOptions(props) {
    // blog tweet export delete
    const quest = props.quest
    const { openRight } = useOpenRight()

    const openStoryModal = useStore(state => state.actions.modalActions.openStoryModal)
    const openExportQuestModal = useStore(state => state.actions.modalActions.openExportQuestModal)
    const openImportTextModal = useStore(state => state.actions.modalActions.openImportTextModal)
    const middleQuest = useStore(state => state.middleQuest)
    const middleQuests = useStore(state => state.middleQuests)
    const set = useStore(state => state.set)

    const [showConfirmTweet, setShowConfirmTweet] = useState(false)
    const [showConfirmDelete, setShowConfirmDelete] = useState(false)
    const [transcribing, setTranscribing] = useState(false)

    const canDelete = () => perms.canDeleteQuest(gon.currentUser, quest)
    const isModerationDelete = () => canDelete() && !perms.amQuestOwner(quest)
    const canExport = () => perms.amQuestOwner(quest)
    const canTweet = () => {
        // Only allow tweeting root threads (not subquests)
        const isRootThread = !quest?.side_quest
        return (
            perms.amQuestOwner(quest) &&
            !!gon?.currentUser?.twitter_providers?.length &&
            isRootThread
        )
    }
    const canDuplicate = () => perms.amQuestOwner(quest)
    const canImport = () => perms.amQuestOwner(quest)
    const canTranscribe = () =>
        gon.currentUser?.features?.some(f => f.name === 'transcribe') && perms.amQuestOwner(quest)

    const canMuteUser = () => {
        // Can mute any user except yourself and system users
        const questUserId = quest?.user?.id || quest?.user_id
        return (
            !!questUserId &&
            !!gon?.currentUser?.id &&
            !quest?.is_system &&
            questUserId !== gon.currentUser.id
        )
    }

    const canMuteQuestNotifications = () => {
        // Can mute notifications for any quest that exists, except system/bot quests
        const questUserId = quest?.user_id || quest?.user?.id
        return !!quest?.id && !!gon?.currentUser?.id && !!questUserId && !quest?.is_system
    }

    const canMuteStream = () => {
        // Can mute streams if quest is in a public team
        const team = quest?.team
        return !!team?.id && team?.public && !!gon?.currentUser?.id
    }

    const handleTweetClick = () => {
        // Check if user needs Twitter media scope update
        const needsUpdate = gon?.currentUser?.options?.twitter_auth_needs_media_scope_update

        if (needsUpdate) {
            // Show TwitterMediaAlert with custom button text
            const { set } = useStore.getState()
            set({
                isModalOpen: true,
                modalParams: {
                    type: 'TwitterMediaAlert',
                    primaryButtonText: 'Go to Settings',
                    secondaryButtonText: 'Tweet without media',
                    secondaryButtonAction: () => {
                        // Close modal and proceed with tweet (without media capabilities)
                        set({ isModalOpen: false })
                        setTimeout(() => setShowConfirmTweet(true), 100)
                    },
                },
            })
        } else {
            // Proceed normally to show ConfirmTweet
            setShowConfirmTweet(true)
        }
    }

    const duplicateQuest = async quest => {
        props.close()
        const [origMiddleQuest, origMiddleQuests] = [middleQuest, middleQuests]
        const { success, quest: dupQuest } = await api.duplicateQuest(quest.id)
        if (success) {
            cacheQuest(dupQuest)
            openRight({ questId: dupQuest.id })
            // TODO PANEL REFACTOR Set Center Panel.
        } else {
            alert('Error duplicating quest :(')
            //set({ middleQuest: origMiddleQuest, middleQuests: origMiddleQuests })
        }
    }
    const transcribeQuestTitle = async (quest, identifySpeakers) => {
        if (transcribing) {
            alert('Transcription in progress.')
        }
        if (!quest?.parent?.transcription) {
            setTranscribing(true)
            const res = await api.transcribeAnswer(quest.parent.id, identifySpeakers)
            if (res?.success) alert('Transcription started.')
            else alert(res.msg)
            setTranscribing(false)
        }
    }

    /** @type {(OptionItem | CustomOptionItem)[]} */
    let options = []

    if (quest.is_draft && canImport()) {
        /*
        // TODO Open Panel using useStreamFilters.
        options.push({
            type: 'icon',
            name: 'Import',
            value: 'import',
            desc: 'Import text as thread.',
            logo: 'fa fa-upload',
            callback: () => openImportTextModal(quest),
        })
        */
    } else {
        options = [
            ...(!props.hidePlayOption
                ? [
                      {
                          type: 'icon',
                          name: 'Play',
                          value: 'play',
                          icon: <Play size={16} />,
                          callback: () => {
                              window.logEv('PLAY_THREAD_OPEN', {
                                  quest_id: quest?.id,
                                  source: 'thread_actions',
                              })
                              openStoryModal(quest)
                          },
                      },
                  ]
                : []),
        ]

        const isCurrentUserProfile =
            !!gon.currentUser &&
            !!props.filter?.user &&
            String(props.filter.user) === String(gon.currentUser.id)
        const isPinnedToProfile =
            isCurrentUserProfile &&
            !!gon.currentUser?.pinned_quest_id &&
            String(gon.currentUser.pinned_quest_id) === String(quest.id)
        const refreshCurrentStream = async () => {
            if (props.filter) {
                const keyedFilter = withDepth(props.filter)
                const queryKey = getQuestsQueryKey(keyedFilter)
                resetNaturalFeedSessionSnapshot(String(queryKey[1]))
                await queryClient.invalidateQueries({
                    queryKey,
                    exact: true,
                })
            } else {
                await queryClient.invalidateQueries({ queryKey: ['quests'] })
            }
        }

        if (isCurrentUserProfile) {
            const onProfilePinToggle = async () => {
                const res = isPinnedToProfile
                    ? await api.unpinQuest(quest.id)
                    : await api.pinQuest(quest.id)
                if (!res?.ok) {
                    showModal(
                        isPinnedToProfile
                            ? 'Failed to unpin thread from your profile.'
                            : 'Failed to pin thread to your profile.',
                        'error',
                    )
                    return
                }

                gon.currentUser.pinned_quest_id = isPinnedToProfile ? null : quest.id
                await refreshCurrentStream()
                props.close && props.close()
            }

            options.unshift(
                /** @type {CustomOptionItem} */ ({
                    customComp: (
                        <div className={styles.optionComp}>
                            <div className={styles.optionContainer}>
                                <div
                                    className={styles.option}
                                    onClick={ev => {
                                        ev.preventDefault()
                                        ev.stopPropagation()
                                        onProfilePinToggle()
                                    }}
                                >
                                    <span className={styles.optionIcon}>
                                        <Pin size={16} />
                                    </span>
                                    <span className={styles.name}>
                                        {isPinnedToProfile
                                            ? 'Unpin thread from Profile'
                                            : 'Pin thread to Profile'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ),
                }),
            )
        }

        // Pin/Unpin thread to stream (team) for team admins
        const team = quest.team
        const isTeamAdmin = team && gon.currentUser && gon.currentUser.id === team.admin_id
        const isPinned = team && quest.id === team.quest_id
        if (team && isTeamAdmin) {
            const onPinToggle = async () => {
                if (isPinned) await useStore.getState().unpinQuestFromTeam(team.id, quest)
                else await useStore.getState().pinQuestToTeam(team.id, quest)
                // Do not invalidate quest lists; user can refresh manually to see latest
                props.close && props.close()
            }

            options.unshift(
                /** @type {CustomOptionItem} */ ({
                    customComp: (
                        <div className={styles.optionComp}>
                            <div className={styles.optionContainer}>
                                <div
                                    className={styles.option}
                                    onClick={ev => {
                                        ev.preventDefault()
                                        ev.stopPropagation()
                                        onPinToggle()
                                    }}
                                >
                                    <span className={styles.optionIcon}>
                                        <Pin size={16} />
                                    </span>
                                    <span className={styles.name}>
                                        {isPinned
                                            ? 'Unpin thread from Channel'
                                            : 'Pin thread to Channel'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ),
                }),
            )
        }

        if (canMuteUser()) {
            const userId = quest?.user?.id || quest?.user_id
            if (userId) {
                const isMuted = isUserMuted(userId)
                const userName = quest.parent?.user?.name || quest.user?.name
                options.push({
                    type: 'icon',
                    name: isMuted ? 'Unmute User' : 'Mute User',
                    value: isMuted ? 'unmute_user' : 'mute_user',
                    desc: userName,
                    icon: isMuted ? <Volume2 size={16} /> : <VolumeX size={16} />,
                    callback: () => handleMuteUser(isMuted),
                })
            }
        }

        if (canMuteQuestNotifications()) {
            const isQuestMutedState = isQuestMuted(quest.id)
            options.push({
                type: 'icon',
                name: isQuestMutedState
                    ? 'Resume Thread Notifications'
                    : 'Mute Thread Notifications',
                value: isQuestMutedState ? 'unmute_quest' : 'mute_quest',
                icon: isQuestMutedState ? <Bell size={16} /> : <BellOff size={16} />,
                callback: () => handleMuteQuest(isQuestMutedState),
            })
        }

        if (canMuteStream()) {
            const streamId = quest?.team?.id
            const isStreamMutedState = isStreamMuted(streamId)
            const streamName = quest?.team?.name || 'this channel'
            options.push(
                /** @type {CustomOptionItem} */ ({
                    customComp: (
                        <div className={styles.optionComp}>
                            <div className={styles.optionContainer}>
                                <div
                                    className={styles.option}
                                    onClick={ev => {
                                        ev.preventDefault()
                                        ev.stopPropagation()
                                        props.close && props.close()
                                        handleMuteStream(isStreamMutedState)
                                    }}
                                >
                                    <span className={styles.optionIcon}>
                                        {isStreamMutedState ? (
                                            <Volume2 size={16} />
                                        ) : (
                                            <VolumeX size={16} />
                                        )}
                                    </span>
                                    <span className={styles.name}>
                                        {isStreamMutedState ? 'Unmute Channel' : 'Mute Channel'}
                                    </span>
                                    <span className={styles.description}>{streamName}</span>
                                </div>
                            </div>
                        </div>
                    ),
                }),
            )
        }

        if (canTweet()) {
            // Check if quest has already been tweeted
            const isTweeted = quest?.parent?.tweet && Object.keys(quest.parent.tweet).length > 0

            if (isTweeted) {
                // Show tweeted state as a proper option
                options.push({
                    type: 'icon',
                    name: 'This message was tweeted',
                    value: 'tweeted',
                    icon: <Send size={16} />,
                    callback: () => {
                        // Open tweet URL if available
                        const tweetUrl = quest?.parent?.tweet?.url
                        if (tweetUrl) {
                            window.open(tweetUrl, '_blank')
                        }
                    },
                })
            } else {
                options.push({
                    type: 'icon',
                    name: 'Tweet',
                    value: 'tweet',
                    icon: <Send size={16} />,
                    closeOnClick: false,
                    callback: handleTweetClick,
                })
            }
        }

        const hasExportFeature = useUserHasFeature('export_thread')
        if (canExport() && hasExportFeature) {
            options.push({
                type: 'icon',
                name: 'Export',
                value: 'export',
                icon: <Download size={16} />,
                callback: () => openExportQuestModal(quest),
            })
        }

        if (canTranscribe()) {
            options.push({
                type: 'icon',
                name: 'Transcribe',
                value: 'transcribe',
                icon: <Mic size={16} />,
                callback: () => transcribeQuestTitle(quest, false),
            })
        }

        if (canTranscribe()) {
            options.push({
                type: 'icon',
                name: 'Identify speakers',
                value: 'identifyspeakers',
                icon: <MessageSquare size={16} />,
                callback: () => transcribeQuestTitle(quest, true),
            })
        }
        const hasDuplicateFeature = useUserHasFeature('duplicate_thread')
        if (canDuplicate() && hasDuplicateFeature) {
            // TODO PANEL REFACTOR Set Center Panel.
            options.push({
                type: 'icon',
                name: 'Duplicate',
                value: 'duplicate',
                icon: <Copy size={16} />,
                callback: () => duplicateQuest(quest),
            })
        }

        if (!props.hideDeleteOption && canDelete()) {
            options.push({
                type: 'icon',
                name: 'Delete',
                value: 'delete',
                desc: 'Delete thread',
                icon: <Trash2 size={16} />,
                closeOnClick: false,
                callback: () => {
                    if (isModerationDelete() && props.deleteHandler) {
                        props.deleteHandler()
                    } else {
                        setShowConfirmDelete(true)
                    }
                },
            })
        }
    }

    const postTweet = async (providerId, ev) => {
        ev.preventDefault()
        ev.stopPropagation()
        logEv('TWEET_QUEST')

        props.close()

        // Show a loading notification that tweet is being posted
        const { set } = useStore.getState()
        const openConfirmModal = useStore.getState().actions.modalActions.openConfirmModal

        // Show loading modal with spinning icon
        openConfirmModal(
            <div style={{ textAlign: 'center' }}>
                <i
                    className="fa fa-circle-o-notch fa-spin fa-2x"
                    style={{ marginBottom: '15px', color: '#1da1f2' }}
                />
                <div>Posting your thread to Twitter...</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                    This may take a few moments
                </div>
            </div>,
            '',
            () => {},
            { hideCancel: true, hideConfirm: true },
        )

        try {
            const data = await props.tweet(providerId)
            if (data && !data.success) {
                // Close loading modal
                set({ isModalOpen: false })

                if (data.error) {
                    // Show error modal after a brief delay
                    setTimeout(() => {
                        set({
                            isModalOpen: true,
                            modalParams: {
                                type: 'TwitterAuthError',
                                message: data.error,
                            },
                        })
                    }, 100)
                } else if (data.failed) {
                    console.error('Tweet validation failed:', data.failed)
                    // Close loading modal and show error
                    set({ isModalOpen: false })
                    setTimeout(() => {
                        openConfirmModal(
                            'Unable to post thread. Some tweets may be invalid.',
                            'OK',
                            () => {},
                            { iconType: 'error', hideCancel: true },
                        )
                    }, 100)
                }
            }
            // If successful, the socket message will handle showing success modal and closing loading
        } catch (err) {
            console.error('Tweet request failed:', err)
            // Close loading modal
            set({ isModalOpen: false })
            setTimeout(() => {
                openConfirmModal('Failed to post thread. Please try again.', 'OK', () => {}, {
                    iconType: 'error',
                    hideCancel: true,
                })
            }, 100)
        }
    }

    // Check old master for how to use this. I think it prevents you from tweeting a quest with unprocessed video.
    const webmCountCheck = quest => {
        return (
            quest.sorted_answers.map(a => a.recording_type).filter(type => type == 'video/webm')
                .length == 0
        )
    }

    const isMuteOption = (/** @type {OptionItem | CustomOptionItem} */ opt) =>
        MUTE_OPTION_VALUES.includes(/** @type {OptionItem} */ (opt).value)

    if (props.onlyMuteOptions) {
        options = options.filter(isMuteOption)
    } else if (props.hideMuteOptions) {
        options = options.filter(opt => !isMuteOption(opt))
    }

    const showOptionList = !showConfirmDelete && !showConfirmTweet

    const openConfirmModal = useStore.getState().actions.modalActions.openConfirmModal

    const refreshMuteSurfaces = async () => {
        await queryClient.invalidateQueries({ queryKey: ['quests'] })
        const { activeSpaceId } = useStore.getState()
        if (!activeSpaceId) return

        const numNoti = await api.getNotificationsCount(activeSpaceId)
        useStore.getState().set(state => {
            state.numNoti = numNoti
            state.spaceNotiCounts = { ...state.spaceNotiCounts, [activeSpaceId]: numNoti }
        })
    }

    const handleMuteUser = isCurrentlyMuted => {
        if (isCurrentlyMuted) {
            // Handle unmute
            openConfirmModal(
                `Are you sure you want to unmute ${quest.parent?.user?.name}?`,
                'Unmute',
                async () => {
                    try {
                        await api.unmuteUser(quest.user_id)
                        removeMutedUser(quest.user_id)
                        await refreshMuteSurfaces()
                        showModal(`${quest.parent?.user?.name || 'User'} unmuted.`)
                    } catch (error) {
                        console.error('Failed to unmute user:', error)
                    }
                },
            )
        } else {
            // Handle mute
            openConfirmModal(
                `Are you sure you want to mute ${quest.parent?.user?.name}?`,
                'Mute',
                async () => {
                    try {
                        if (props.muteQuestUser) {
                            await props.muteQuestUser()
                        } else {
                            await api.muteUser(quest.user_id)
                        }
                        addMutedUser(quest.user_id)
                        await refreshMuteSurfaces()
                        showModal(`${quest.parent?.user?.name || 'User'} muted.`)
                    } catch (error) {
                        console.error('Failed to mute user:', error)
                    }
                },
            )
        }
    }

    const showModal = (message, iconType = 'success') => {
        openConfirmModal(message, 'OK', () => {}, { iconType, hideCancel: true })
    }

    const handleMuteQuestApiCall = async (apiCall, onSuccess, errorPrefix) => {
        try {
            const response = await apiCall()
            if (response.ok) {
                const data = await response.json()
                if (data.success) {
                    onSuccess()
                } else {
                    showModal(`${errorPrefix}: ${data.error || 'Unknown error'}`, 'error')
                }
            } else {
                const data = await response.json().catch(() => ({}))
                showModal(`${errorPrefix}: ${data.error || 'Server error'}`, 'error')
            }
        } catch (error) {
            console.error(`${errorPrefix}:`, error)
            showModal(`${errorPrefix}: Network error`, 'error')
        }
    }

    const handleMuteQuest = isCurrentlyMuted => {
        const question = isCurrentlyMuted
            ? 'Resume notifications for this thread?'
            : 'Stop receiving notifications for this thread?'
        const buttonText = isCurrentlyMuted ? 'Resume' : 'Mute'

        openConfirmModal(question, buttonText, async () => {
            const apiCall = isCurrentlyMuted
                ? () => api.unmuteQuest(quest.id)
                : () => api.muteQuest(quest.id)

            const onSuccess = isCurrentlyMuted
                ? () => {
                      removeMutedQuest(quest.id)
                      showModal('Notifications resumed for this thread.')
                  }
                : () => {
                      addMutedQuest(quest.id)
                      showModal('Thread notifications muted. You can unmute in thread options.')
                  }

            const errorPrefix = isCurrentlyMuted ? 'Failed to unmute' : 'Failed to mute'
            await handleMuteQuestApiCall(apiCall, onSuccess, errorPrefix)
        })
    }

    const handleMuteStream = isCurrentlyMuted => {
        const streamName = quest?.team?.name || 'this channel'
        const question = isCurrentlyMuted ? `Unmute "${streamName}"?` : `Mute "${streamName}"?`
        const buttonText = isCurrentlyMuted ? 'Unmute' : 'Mute'

        openConfirmModal(question, buttonText, async () => {
            const streamId = quest?.team?.id
            const apiCall = isCurrentlyMuted
                ? () => api.unmuteStream(streamId)
                : () => api.muteStream(streamId)

            const onSuccess = isCurrentlyMuted
                ? () => {
                      removeMutedStream(streamId)
                      showModal(`"${streamName}" unmuted.`)
                  }
                : () => {
                      addMutedStream(streamId)
                      showModal(`"${streamName}" muted.`)
                  }

            const errorPrefix = isCurrentlyMuted
                ? 'Failed to unmute channel'
                : 'Failed to mute channel'
            await handleMuteQuestApiCall(apiCall, onSuccess, errorPrefix)
        })
    }

    return (
        <div className={styles.extendedOptionsComp}>
            {showOptionList && (
                <>
                    {!props.hideHeader && (
                        <QuestOptionsHeader
                            title="Thread Actions"
                            close={props.close}
                            compact={props.compact}
                        />
                    )}

                    <OptionList
                        options={options}
                        close={props.close}
                        contextStyles={props.listContextStyles}
                    />
                </>
            )}

            {showConfirmDelete && (
                <div className={styles.confirmDeleteContainer}>
                    <ConfirmDelete
                        back={() => setShowConfirmDelete(false)}
                        deleteHandler={props.deleteHandler}
                    />
                </div>
            )}

            {showConfirmTweet && (
                <div className={styles.confirmTweetContainer}>
                    <ConfirmTweet back={() => setShowConfirmTweet(false)} postTweet={postTweet} />
                </div>
            )}
        </div>
    )
}

function ConfirmDelete(props) {
    const back = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (props.back) props.back()
    }

    const deleteHandler = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        props.deleteHandler()
    }

    return (
        <div>
            <div className={styles.confirmDeleteComp}>
                <div className={styles.confirmHeaderContainer}>
                    <div className={styles.backBtn} onClick={back}>
                        <i className="fa fa-arrow-left" />
                    </div>

                    <div className={styles.confirmTitle}>Confirm Delete</div>
                </div>

                <div className={styles.confirmDeleteBtn} onClick={deleteHandler}>
                    <i className="fa fa-close" />
                    <span className={styles.confirmLabel}> Delete Thread </span>
                </div>
            </div>
        </div>
    )
}

function ConfirmTweet(props) {
    const showAccounts = !!gon?.currentUser?.twitter_providers?.length

    const back = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (props.back) props.back()
    }

    return (
        <div className={styles.confirmTweetComp}>
            {showAccounts && (
                <div className={styles.twitterAccountContainer}>
                    <div className={styles.twitterAccounts}>
                        <div className={styles.twitterAccountsHeader}>
                            <div className={styles.backBtn} onClick={back}>
                                <i className="fa fa-arrow-left" />
                            </div>

                            <div className={styles.twitterTitle}>Tweet from account:</div>
                        </div>

                        {gon.currentUser.twitter_providers.map(twitter => {
                            return (
                                <div
                                    key={twitter.id}
                                    className={styles.twitterUserContainer}
                                    onClick={ev => props.postTweet(twitter.id, ev)}
                                >
                                    <div className={styles.twitterProfileImage}>
                                        <img
                                            src={
                                                twitter.image_url ||
                                                'http://abs.twimg.com/sticky/default_profile_images/default_profile_normal.png'
                                            }
                                        ></img>
                                    </div>
                                    <div className={styles.twitterUsername}>{twitter.username}</div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}
